import React, { memo, useRef, useEffect, useState, useMemo, useContext } from 'react'
import { useHistory } from 'react-router-dom'

import isEqual from 'lodash/isEqual'
import { Protocol } from "pmtiles/dist/index.js";
import maplibregl from 'maplibre-gl';
import { LngLatBounds  } from 'maplibre-gl/dist/maplibre-gl'

import tilebelt from '@mapbox/tilebelt'

import settings from '../../conf'
import MapContext from './MapContext'
import { getViewFromHash, getBestFitBearing, mapHash } from './utils'
import { layers as baseLayers } from './styles/waypoint.js'
import baselayers from './styles/baselayers.json'

import Loading from '../Loading'

import { useSettings } from '../../slices/settings'

import { isWebglSupported  } from '../../utils'

import {
  useSiteMapsetting
} from '../../api'

const SUPPORTED = isWebglSupported()


const style = {
  version: 8,
  glyphs: settings.STATIC_URL + 'maps/fonts/{fontstack}/{range}.pbf',
  sources: {
      "base": {
        type: "vector",
        url: "pmtiles://" + settings.BASEMAP_URL
      },
      "dem": {
        type: "raster-dem",
        encoding: "terrarium",
        url: "pmtiles://" + settings.DEM_URL
      },
      "terrain": {
        type: "raster-dem",
        encoding: "terrarium",
        url: "pmtiles://" + settings.DEM_URL
      },
  },
  layers: baselayers
}

const protocol = new Protocol();
maplibregl.addProtocol("pmtiles", protocol.tile);

const Map = (props) => {
  const { siteId = settings.SITE_ID, extent, view, padding} = props
  const [map, setMap] = useState()
  const [isReady, setIsReady] = useState(false)
  const [isMove, setIsMove] = useState(false)
  const { zoom } = useSettings()
  const history = useHistory()
  const siteMapSettings = useSiteMapsetting({ siteId: siteId })
  const container = useRef()
  const ref = useRef()

  useEffect(() => {
    ref.current = {
      padding: padding,
      bounds: extent ? extent.bounds : null,
      bestFitBaering: extent ? extent.bestFitBaering : null,
      map: map
    }
  }, [map, padding, extent])

  useEffect(() => {
    if (!siteMapSettings || !SUPPORTED) return

    const { padding, bestFitBaering, bounds, map } = ref.current
    const hash = history.location.hash

    if (map) {
      map.remove()
    }

    setIsReady(false)

    const options = {
      container: container.current,
      style: style,
      maxZoom: 17,
      trackResize: true,
      clickTolerance: 10,
      doubleClickZoom: true,
      scrollZoom: true,
      hash: false,
      maxPitch: 60,
      fitBoundsOptions: { padding: padding },
      customAttribution: '@forwalk.org mapdata @openstreetmap.org',
      transformRequest: (url, resourceType) => {
        // fix temporanneo finche non faccio nuovo server locale
        if (resourceType === 'Tile' && url.endsWith('.mvt')){
          return {
            url: url,
            credentials: 'include'
          }
        }
      }
    }

    if (hash && mapHash.test(hash)) {
      const [lat, lng, zoom, bearing] = getViewFromHash(hash)
      options.center = [lat, lng]
      options.zoom = zoom
      options.bearing = bearing
    } else {
      options.bounds = bounds || siteMapSettings.extent
      if (bestFitBaering) {
        options.bearing = getBestFitBearing(
          container,
          padding,
          options.bounds,
          bestFitBaering
        )
      }
    }

    const mapObj = new maplibregl.Map(options)
    const e = siteMapSettings.extent
    mapObj.maxBounds = new LngLatBounds([
      [e[0], e[1]],
      [e[2], e[3]]
    ])
    if (padding) mapObj.setPadding(padding)

    mapObj.on('movestart', (e) => setIsMove(true))
    mapObj.on('moveend', (e) => setIsMove(false))
    mapObj.on('pitchend', (e) => {
      const hasPitch = mapObj.getPitch()
      const hasTerrain = mapObj.getTerrain()
      if (!hasPitch && hasTerrain) {
        mapObj.setTerrain(null)
      }
    })
    mapObj.on('pitchstart', (e) => {
      const hasTerrain = mapObj.getTerrain()
      if (!hasTerrain) {
        mapObj.setTerrain({
          source: 'terrain',
          exaggeration: 2
        })
      }
    })

    mapObj.once('load', () => {
      mapObj.addSource('baselayer', {
        type: 'vector',
        tiles: [
          siteMapSettings.source_url
        ],
        minzoom: 4,
        maxzoom: 18
      })

      const baselayerSource = mapObj.getSource('baselayer')
      const quadkeys = siteMapSettings.tiles.map(tileid => tilebelt.tileToQuadkey(tileid))

      baselayerSource.tileBounds = {
        contains: (tileid) => {
          const quadkey = tilebelt.tileToQuadkey([tileid.x, tileid.y, tileid.z])
          for (const q of quadkeys) {
            const len = Math.min(quadkey.length, q.length)
            if (q.substring(0, len) === quadkey.substring(0, len)) {
              return true
            }
          }
          return false
        }
      }

      setMap(mapObj)
    })
    mapObj.showTileBoundaries = settings.DEBUG
    mapObj.showPadding = settings.DEBUG
  }, [history, siteMapSettings])

  useEffect(()=>{
    if (!map) return 

    for (const layer of baseLayers(zoom)) {
      const mapLayer = map.getLayer(layer.id)
      if (mapLayer) {
        for (const props in layer.layout){
            map.setLayoutProperty(layer.id, props, layer.layout[props])
        }
        for (const props in layer.paint){
            map.setPaintProperty(layer.id, props, layer.paint[props])
        }
      } else {
        map.addLayer(layer)
      }
    }

    setIsReady(true)
    
  }, [map, zoom])

  useEffect(()=>{
    if (!map || isMove || !padding || isEqual(map.getPadding(), padding)) return
    map.flyTo({padding: padding})
  }, [map, padding, isMove])

  useEffect(() => {
    const {bounds, bestFitBaering} = extent || {}
    const { padding } = ref.current

    if (!map || !bounds) return

    if (padding) map.setPadding(padding)

    const options = {
      maxZoom: 15,
    }

    if (bestFitBaering) {
      options.bearing = getBestFitBearing(
        container, 
        padding, 
        bounds, 
        bestFitBaering
      )
    } else {
      options.bearing = map.getBearing()
    }

    map.fitBounds(bounds, options, {originalEvent: true})
  }, [map, extent])

  useEffect(() => {
    if (!map || !view) return

    const options = {
      center: view.lnglat || map.getCenter(),
      zoom: Math.max(view.zoom || 0,  map.getZoom()),
      bearing: view.bearing || map.getBearing(),
    }

    map.flyTo(options)
  }, [map, view])

  return useMemo(() => {
    return (
      <div ref={container} className="map">
        <MapContext.Provider value={map}>
          { !isReady ? <Loading/> : props.children }
        </MapContext.Provider>
      </div>
    )
  }, [map, props.children, isReady])
}

export default Map
